import React, { useCallback, useRef, useState } from "react";

import AlertCircle from "@uui/AlertCircle";
import { isEmpty, toLower, map, trim, uniqBy } from "lodash/fp";

import { Dialog, Divider, Typography } from "@mui/material";

import type { ColorPickerProps } from "@app/analytics/components/ColorPicker";
import Tag from "@app/analytics/components/Tag";
import { getNewTagColor } from "@app/analytics/components/Tag/utils";
import { CreateCustomTagBody, Tag as ITag } from "@app/common/types";

import Stack, { Row } from "@rui/Stack";

import SelectableTag from "./SelectableTag";
import {
  ActionButton,
  PopoverContent,
  PopoverHeader,
  PopoverHeading,
  TagList,
  TagListContainer,
  TextField,
} from "./styles";
import TagInput from "./TagInput";

type ControlProps = { onClick: () => void };

export interface AddCustomTagPopoverProps {
  /** All the user's tags (auto tags will not be displayed,
   * just used to determine if new tag name is a duplicate) */
  allTags: ITag[];
  onSave: (tags: CreateCustomTagBody[]) => void;
  renderControl: (props: ControlProps) => React.ReactNode;
  loading?: boolean;
  title?: string;
}

const AddCustomTagPopover = ({
  allTags,
  onSave,
  renderControl,
  loading,
  title = "Create Custom Tags",
}: AddCustomTagPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [newTag, setNewTag] = useState<CreateCustomTagBody | null>(null);
  const [customTags, setCustomTags] = useState<CreateCustomTagBody[]>([]);

  const focusInputElement = () => inputRef.current?.click();
  const existingTagNames = map((t) => trim(toLower(t.name)), allTags);

  const resetState = useCallback(() => {
    setNewTag(null);
  }, []);

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    const inputValue = event.target.value;
    if (inputValue) {
      setNewTag((tag) => ({
        name: inputValue,
        color: tag?.color ?? getNewTagColor(inputValue, allTags),
      }));
    } else {
      resetState();
    }
  };

  const isDuplicate =
    newTag && existingTagNames.includes(trim(toLower(newTag.name)));

  const handleColorChange: ColorPickerProps["onChange"] = (color) => {
    setNewTag((t) => (t ? { ...t, color: color.backgroundColor } : t));
  };

  const handleOpen = () => {
    setIsOpen(true);
    setCustomTags([]);
    // short timeout to give time for the element to appear before trying to focus it
    setTimeout(focusInputElement, 100);
  };

  const handleClose = () => {
    setIsOpen(false);
    resetState();
  };

  const handleRemoveTag = (tag: ITag) => {
    setCustomTags((tags) => tags.filter((t) => t.name !== tag.name));
  };

  const handleCreateTag = () => {
    if (isDuplicate || !newTag) return;
    setCustomTags((tags) => uniqBy("name", [...tags, newTag]));
    resetState();
    focusInputElement();
  };

  const handleClickSave = () => {
    onSave(customTags);
    handleClose();
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    event
  ) => {
    switch (event.key) {
      case "Escape":
        event.stopPropagation();
        event.preventDefault();
        resetState();
        break;
      case "Enter": {
        event.stopPropagation();
        event.preventDefault();
        if (newTag) {
          handleCreateTag();
        } else if (isEmpty(customTags)) {
          handleClose();
        } else {
          handleClickSave();
        }
        break;
      }
      default:
        break;
    }
  };

  const hasCustomTags = newTag || !isEmpty(customTags);

  return (
    <>
      {renderControl({ onClick: handleOpen })}
      <Dialog open={isOpen} onClose={handleClose}>
        <PopoverContent spacing={1}>
          <PopoverHeader sx={{ borderBottom: "none" }}>
            <PopoverHeading>{title}</PopoverHeading>
          </PopoverHeader>
          <TextField autoFocus>
            <TagInput
              placeholder="Enter a tag name"
              fullWidth
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              ref={inputRef}
              value={newTag?.name ?? ""}
            />
          </TextField>
          {isDuplicate && (
            <Row sx={{ pl: 0.5 }} spacing={0.5} variant="left">
              <AlertCircle sx={{ fontSize: "12px", color: "error.main" }} />
              <Typography variant="labelReg" sx={{ color: "error.main" }}>
                This tag already exists
              </Typography>
            </Row>
          )}
          {hasCustomTags && (
            <TagListContainer spacing={1}>
              {!isDuplicate && newTag && (
                <Stack>
                  <SelectableTag
                    color={newTag.color}
                    name={newTag.name}
                    isNew
                    onClick={handleCreateTag}
                    onColorChange={handleColorChange}
                    sx={{ pb: hasCustomTags ? undefined : 0 }}
                  />
                  {hasCustomTags && <Divider />}
                </Stack>
              )}
              {hasCustomTags && (
                <TagList>
                  {customTags.map((tag) => (
                    <Tag
                      sizing="padded"
                      key={tag.name}
                      onAction={handleRemoveTag}
                      tag={{
                        id: tag.name,
                        kind: "custom",
                        isHidden: false,
                        ...tag,
                      }}
                    />
                  ))}
                </TagList>
              )}
            </TagListContainer>
          )}
          <Row spacing={1}>
            <ActionButton onClick={handleClose}>Discard</ActionButton>
            <ActionButton
              onClick={handleClickSave}
              variant="contained"
              disabled={isEmpty(customTags)}
              loading={loading}
            >
              Save
            </ActionButton>
          </Row>
        </PopoverContent>
      </Dialog>
    </>
  );
};

export default AddCustomTagPopover;
